/**
 * Cards read from a distance. Zoomed out, a card's text and controls shrink with the canvas until
 * they can be neither read nor pressed, while the card itself is often still big enough on the
 * screen to work in. So a card's inside is zoomed back up in steps (CSS zoom on its head, its body
 * and the strip under it: index.css, "Cards read from a distance"): its text stays at least
 * LEAST_READABLE of its size at 100% while the steps last, the card keeps its place and size on
 * the canvas, and what no longer fits in it scrolls. Steps, not a counter-zoom that follows the canvas: every change
 * lays out every card in view again, so it happens only as the zoom crosses a line, and a little
 * above the line on the way back, so a zoom hovering at a line does not flip the cards back and
 * forth.
 *
 * The canvas sets the step on its content layer (`--card-zoom`, InfiniteCanvas) and here, for
 * what measures a card's content (hooks/useFitNodeHeight.ts).
 */

/**
 * What a card's inside is zoomed by, from the smallest step up; 1 is the card as drawn at 100%.
 * It stops at 1.5, reached below 64%: zoomed up more, a card's settings no longer fit in it
 * (its picture, references and prompt take the room), so from 53% down the inside stays at 1.5
 * and shrinks with the canvas, as every card did before the steps; below 30% the cards show a
 * stand-in for their bodies anyway (utils/farZoom.ts).
 */
export const CARD_ZOOM_STEPS: readonly number[] = [1, 1.25, 1.5];
/** A card's text is kept at least this much of its size at 100% zoom: 13px reads as 10.4px or more */
export const LEAST_READABLE = 0.8;
/** On the way back in, a step is left only once the smaller one would read this much better than least readable */
const BACK_MARGIN = 1.06;

const stepReading = (scale: number, least: number): number =>
    CARD_ZOOM_STEPS.find(step => step * scale >= least) ?? CARD_ZOOM_STEPS[CARD_ZOOM_STEPS.length - 1];

/** The step for this zoom, given the step before it: the smallest that keeps the text readable, kept a little longer on the way back in */
export const cardZoomStep = (scale: number, previous: number): number => {
    const wanted = stepReading(scale, LEAST_READABLE);
    if (wanted >= previous) return wanted;
    return Math.min(previous, stepReading(scale, LEAST_READABLE * BACK_MARGIN));
};

let current = 1;

/** Called by the canvas as the zoom crosses a line; what measures a card's content reads it */
export const setCardZoom = (step: number): void => { current = step; };
export const cardZoomNow = (): number => current;
